<template>
  <BottomSheet :title="title" @close="$emit('close')">
    <div v-if="loading" class="ls-loading">Hämtar…</div>
    <p v-else-if="error" class="ls-error">{{ error }}</p>

    <template v-else>
      <!-- ── Fokuserat lyft ── -->
      <div v-if="focus" class="ls-hero" :class="statusClass(focus.status)">
        <div class="ls-hero-top">
          <span class="ls-weight">{{ fmtKg(focus.weight) }} kg <small>× {{ focus.reps }}</small></span>
          <span v-if="focus.estimated1Rm" class="ls-e1rm">est. 1RM {{ fmtKg(focus.estimated1Rm) }} kg</span>
        </div>
        <div class="ls-meta">
          <span>{{ fmtDate(focus.date) }}</span>
          <a v-if="focus.gymSessionId" class="ls-link" href="#" @click.prevent="$emit('open-session', focus.gymSessionId)">pass "{{ focus.sessionName || '#' + focus.gymSessionId }}" ›</a>
          <span v-else class="ls-faint">löst lyft</span>
          <span v-if="focus.isCurrentRecord" class="badge ok">🏆 Gällande rekord</span>
        </div>
        <div v-if="focus.notes" class="ls-notes">📝 {{ focus.notes }}</div>
      </div>
      <div v-else-if="setInfo" class="ls-hero">
        <div class="ls-hero-top">
          <span class="ls-weight">{{ setInfo.weight != null ? fmtKg(setInfo.weight) + ' kg' : '' }} <small v-if="setInfo.reps">× {{ setInfo.reps }}</small></span>
        </div>
        <div v-if="setInfo.notes" class="ls-notes">📝 {{ setInfo.notes }}</div>
        <p class="ls-faint">Statusen sätts på setet och följer med lyftet när passet avslutas.</p>
      </div>

      <!-- ── Status ── -->
      <div class="ls-status">
        <label class="ls-label">Status</label>
        <div class="seg">
          <button v-for="o in STATUS_OPTIONS" :key="o.key" :class="[{ on: status === o.key }, o.key]" @click="status = o.key">{{ o.icon }} {{ o.label }}</button>
        </div>
        <p class="ls-help">{{ STATUS_OPTIONS.find(o => o.key === status)?.help }}</p>
        <label class="ls-label">Kommentar <span class="ls-faint">(valfri, visas i listorna)</span></label>
        <input v-model="note" type="text" maxlength="300" class="ls-input" :placeholder="status === 'excluded' ? 'T.ex. ej godkänt djup, felregistrerat…' : 'T.ex. SM 2026, PB-försök…'" />
        <div class="ls-actions">
          <span v-if="saved" class="ls-saved">✓ Sparat</span>
          <button class="btn-primary" :disabled="saving || !dirty" @click="save">{{ saving ? 'Sparar…' : 'Spara' }}</button>
        </div>
      </div>

      <!-- ── Näst i tur ── -->
      <div v-if="candidates.length" class="ls-cands">
        <div class="ls-cands-head">
          <span class="ls-label">Topplista · {{ reps }} rep{{ reps > 1 ? 's' : '' }}</span>
          <span class="ls-faint">Tryck för att välja ett annat lyft</span>
        </div>
        <button v-for="c in candidates" :key="c.id" class="cand" :class="[{ focus: focus && c.id === focus.id }, statusClass(c.status)]" @click="pick(c)">
          <span class="cand-w">{{ fmtKg(c.weight) }} kg</span>
          <span class="cand-d">{{ fmtDate(c.date) }}</span>
          <span class="cand-s">{{ c.isCurrentRecord ? '🏆 ' : '' }}{{ statusLabel(c.status) }}<span v-if="c.statusNote" class="ls-faint"> · {{ c.statusNote }}</span></span>
        </button>
      </div>
    </template>
  </BottomSheet>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import BottomSheet from './BottomSheet.vue';
import { gymAuthState, refreshIfNeeded } from '../../services/gymTrackerAuth.js';
import api from '../../services/gymTrackerApi.js';

export const STATUS_OPTIONS = [
  { key: 'training', label: 'Träning', icon: '🏋️', help: 'Vanligt träningslyft. Räknas i alla rekordlistor utom Tävling.' },
  { key: 'competition', label: 'Tävling', icon: '🎖️', help: 'Tävlingslyft. Får egen rekordlista och räknas även bland alla giltiga.' },
  { key: 'excluded', label: 'Räknas ej', icon: '⛔', help: 'Fusk, felregistrerat eller ej godkänt. Tas ur rekord, 1RM och PB-höjdpunkter – nästa lyft i topplistan blir rekord.' },
];
const LABELS = { training: 'Träning', competition: 'Tävling', excluded: 'Räknas ej' };
export const statusLabel = s => LABELS[s] || s;

/*
 * Bottenark för rekordstatus. Två lägen:
 *  - lyft:  { exerciseTypeId, exerciseTypeName, reps, liftId }  → detaljer + topplista ("näst i tur")
 *  - set:   { setId, set, exerciseTypeId, exerciseTypeName, reps? } → status på setet (aktivt/avslutat pass)
 * Emits: close, changed (efter sparning), open-session(id), set-updated({setId,status,statusNote}).
 */
export default {
  name: 'LiftStatusSheet',
  components: { BottomSheet },
  props: {
    context: { type: Object, required: true },
    mode: { type: String, default: 'all' },
  },
  emits: ['close', 'changed', 'open-session', 'set-updated'],
  setup(props, { emit }) {
    const loading = ref(true);
    const error = ref('');
    const candidates = ref([]);
    const focus = ref(null);
    const status = ref('training');
    const note = ref('');
    const saving = ref(false);
    const saved = ref(false);

    const reps = computed(() => focus.value?.reps ?? props.context.reps ?? props.context.set?.reps ?? null);
    const setInfo = computed(() => (props.context.setId ? { ...(props.context.set || {}), id: props.context.setId } : null));
    const title = computed(() => {
      const name = props.context.exerciseTypeName || focus.value?.exerciseTypeName || 'Lyft';
      return reps.value ? `${name} · ${reps.value} rep${reps.value > 1 ? 's' : ''}` : name;
    });
    const dirty = computed(() => {
      const cur = focus.value || setInfo.value;
      return !!cur && (status.value !== (cur.status || 'training') || (note.value || '') !== (cur.statusNote || ''));
    });

    async function withToken(fn) { await refreshIfNeeded(); return fn(gymAuthState.token); }

    async function load() {
      loading.value = true; error.value = '';
      try {
        if (props.context.exerciseTypeId && reps.value) {
          const d = await withToken(t => api.getPbCandidates({ exerciseTypeId: props.context.exerciseTypeId, reps: reps.value, limit: 8, mode: props.mode }, t));
          candidates.value = d.pbCandidates || [];
        }
        if (props.context.setId) {
          // Set-läge: lyftet (om passet är avslutat) hittas via gymSessionSetId.
          const l = candidates.value.find(c => c.gymSessionSetId === props.context.setId);
          focus.value = l || null;
          const cur = l || props.context.set || {};
          status.value = cur.status || 'training';
          note.value = cur.statusNote || '';
        } else {
          let l = candidates.value.find(c => c.id === props.context.liftId) || candidates.value.find(c => c.isCurrentRecord) || null;
          if (!l && props.context.liftId) {
            const d = await withToken(t => api.getLiftDetail(props.context.liftId, t));
            l = d.liftDetail;
          }
          focus.value = l;
          status.value = l?.status || 'training';
          note.value = l?.statusNote || '';
        }
      } catch (e) { error.value = e.message || String(e); }
      finally { loading.value = false; }
    }

    function pick(c) {
      focus.value = c;
      status.value = c.status || 'training';
      note.value = c.statusNote || '';
      saved.value = false;
    }

    async function save() {
      saving.value = true; saved.value = false; error.value = '';
      try {
        const n = note.value.trim() || null;
        if (focus.value) {
          await withToken(t => api.setLiftStatus({ id: focus.value.id, status: status.value, note: n }, t));
        } else if (props.context.setId) {
          await withToken(t => api.setSessionSetStatus({ setId: props.context.setId, status: status.value, note: n }, t));
          // Föräldern äger set-objektet (reaktivt pass) och uppdaterar det själv.
          emit('set-updated', { setId: props.context.setId, status: status.value, statusNote: n });
        }
        saved.value = true;
        const keepId = focus.value?.id;
        await load();
        if (keepId) { const again = candidates.value.find(c => c.id === keepId); if (again) focus.value = again; }
        emit('changed');
      } catch (e) { error.value = e.message || String(e); }
      finally { saving.value = false; }
    }

    const fmtKg = v => Number(v).toLocaleString('sv-SE', { maximumFractionDigits: 1 });
    const fmtDate = iso => new Date(iso).toLocaleDateString('sv-SE', { dateStyle: 'medium' });
    const statusClass = s => `st-${s || 'training'}`;

    watch(() => props.context, load, { deep: false });
    onMounted(load);

    return { loading, error, candidates, focus, setInfo, status, note, saving, saved, dirty, reps, title, STATUS_OPTIONS, pick, save, fmtKg, fmtDate, statusClass, statusLabel };
  },
};
</script>

<style scoped>
.ls-loading, .ls-error { font-size: 0.9rem; color: var(--gym-text-muted); padding: 0.5rem 0; margin: 0; }
.ls-error { color: var(--gym-danger); }
.ls-faint { color: var(--gym-text-faint); font-size: 0.78rem; font-weight: 500; }
.ls-label { display: block; font-size: 0.78rem; font-weight: 700; color: var(--gym-text-muted); text-transform: uppercase; letter-spacing: 0.03em; }

.ls-hero { background: var(--gym-surface-2); border: 1px solid var(--gym-border); border-left: 4px solid var(--gym-accent); border-radius: var(--gym-radius); padding: 0.8rem 1rem; display: flex; flex-direction: column; gap: 0.35rem; }
.ls-hero.st-competition { border-left-color: var(--gym-warning); }
.ls-hero.st-excluded { border-left-color: var(--gym-danger); opacity: 0.85; }
.ls-hero-top { display: flex; justify-content: space-between; align-items: baseline; gap: 0.6rem; flex-wrap: wrap; }
.ls-weight { font-size: 1.7rem; font-weight: 800; color: var(--gym-accent); line-height: 1; }
.ls-weight small { font-size: 0.95rem; color: var(--gym-text-muted); font-weight: 700; }
.ls-e1rm { font-size: 0.82rem; color: var(--gym-text-muted); font-weight: 600; }
.ls-meta { display: flex; gap: 0.6rem; align-items: center; flex-wrap: wrap; font-size: 0.85rem; color: var(--gym-text); }
.ls-link { color: var(--gym-accent); text-decoration: none; font-weight: 600; }
.ls-link:hover { text-decoration: underline; }
.ls-notes { font-size: 0.85rem; color: var(--gym-text-muted); font-style: italic; }
.badge { font-size: 0.7rem; font-weight: 700; padding: 0.15rem 0.5rem; border-radius: var(--gym-radius-pill); }
.badge.ok { background: var(--gym-success-soft); color: var(--gym-success); }

.ls-status { display: flex; flex-direction: column; gap: 0.45rem; }
.seg { display: grid; grid-template-columns: repeat(3, 1fr); border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm); overflow: hidden; }
.seg button { border: none; border-right: 1px solid var(--gym-border); background: var(--gym-surface-2); color: var(--gym-text-muted); padding: 0.55rem 0.4rem; font-size: 0.86rem; font-weight: 700; cursor: pointer; transition: background var(--gym-dur) var(--gym-ease), color var(--gym-dur) var(--gym-ease); }
.seg button:last-child { border-right: none; }
.seg button.on.training { background: var(--gym-accent); color: var(--gym-on-accent); }
.seg button.on.competition { background: var(--gym-warning); color: #1a1200; }
.seg button.on.excluded { background: var(--gym-danger); color: #fff; }
.ls-help { margin: 0; font-size: 0.78rem; color: var(--gym-text-faint); min-height: 1.2em; }
.ls-input { padding: 0.55rem 0.75rem; border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm); background: var(--gym-surface-2); color: var(--gym-text); font-size: 0.9rem; width: 100%; box-sizing: border-box; }
.ls-input:focus { outline: none; border-color: var(--gym-accent); }
.ls-actions { display: flex; justify-content: flex-end; align-items: center; gap: 0.8rem; }
.ls-saved { color: var(--gym-success); font-weight: 700; font-size: 0.85rem; }
.btn-primary { background: var(--gym-accent); color: var(--gym-on-accent); border: none; padding: 0.55rem 1.2rem; border-radius: var(--gym-radius-sm); font-size: 0.9rem; font-weight: 700; cursor: pointer; }
.btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-primary:hover:not(:disabled) { background: var(--gym-accent-hover); }

.ls-cands { display: flex; flex-direction: column; gap: 0.35rem; }
.ls-cands-head { display: flex; justify-content: space-between; align-items: baseline; gap: 0.5rem; flex-wrap: wrap; }
.cand { display: grid; grid-template-columns: 5.5rem 6.5rem 1fr; gap: 0.5rem; align-items: center; text-align: left; border: 1px solid var(--gym-border); border-left: 4px solid var(--gym-accent); border-radius: var(--gym-radius-sm); background: var(--gym-surface-2); color: var(--gym-text); padding: 0.45rem 0.7rem; font-size: 0.85rem; cursor: pointer; }
.cand:hover { border-color: var(--gym-accent-border); }
.cand.focus { background: var(--gym-accent-softer); border-color: var(--gym-accent-border); }
.cand.st-competition { border-left-color: var(--gym-warning); }
.cand.st-excluded { border-left-color: var(--gym-danger); opacity: 0.7; }
.cand-w { font-weight: 800; color: var(--gym-accent); font-variant-numeric: tabular-nums; }
.cand-d { color: var(--gym-text-muted); font-size: 0.8rem; }
.cand-s { font-size: 0.8rem; color: var(--gym-text); min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

@media (max-width: 600px) {
  .cand { grid-template-columns: 4.6rem 5.4rem 1fr; padding: 0.45rem 0.55rem; }
  .seg button { font-size: 0.8rem; padding: 0.55rem 0.2rem; }
}
</style>
